import type { IMetricTier, MetricTierColor } from '@skystone/shared';

// ---------------------------------------------------------------------------
// Colour tokens
//
// Maps each `colorToken` to a Tailwind chip class. Defined at module scope so
// it is not recreated per render (S7721).
// ---------------------------------------------------------------------------

const TIER_BADGE_CLASS: Record<MetricTierColor, string> = {
  success: 'bg-success/15 text-success',
  primary: 'bg-primary/15 text-primary',
  warning: 'bg-warning/15 text-warning',
  error: 'bg-error/15 text-error',
};

// Throughput-specific explanatory paragraph from the HTML guide. Shown in
// place of a tier list when `tiers` is empty.
const THROUGHPUT_FALLBACK_PARAGRAPHS = [
  "Throughput targets are team-specific — they depend on team size, mix of work and product complexity. The most important benchmark is your own team's historical average. Use a rolling 4–8 week average as the baseline.",
  'Track separately by type: Stories, Bugs and Tasks have different effort profiles and mixing them distorts the picture.',
];

// ---------------------------------------------------------------------------
// TierList
// ---------------------------------------------------------------------------

interface TierListProps {
  tiers: IMetricTier[];
}

function TierRow({ tier }: Readonly<{ tier: IMetricTier }>) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={`w-16 flex-shrink-0 rounded px-1 py-0.5 text-center text-[10px] font-bold uppercase tracking-wide ${TIER_BADGE_CLASS[tier.colorToken]}`}
        data-testid={`tier-badge-${tier.tierId}`}
      >
        {tier.label}
      </span>
      <span className="text-sm text-muted-foreground">{tier.description}</span>
    </li>
  );
}

function ThroughputFallback() {
  return (
    <div className="flex flex-col gap-2">
      {THROUGHPUT_FALLBACK_PARAGRAPHS.map((paragraph) => (
        <p key={paragraph.slice(0, 24)} className="text-sm leading-relaxed text-muted-foreground">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

export function TierList({ tiers }: Readonly<TierListProps>) {
  if (tiers.length === 0) {
    return <ThroughputFallback />;
  }
  const sorted = [...tiers].sort((a, b) => a.tierOrder - b.tierOrder);
  return (
    <ul className="flex list-none flex-col gap-1.5" aria-label="Industry benchmarks">
      {sorted.map((tier) => (
        <TierRow key={tier.tierId} tier={tier} />
      ))}
    </ul>
  );
}
